/**
 * mockItemStore.ts
 * Full in-memory CRUD store for Item Master Catalog.
 * Cross-references mockPoStore (price history) and mockGrStore (inventory data).
 */

import { MASTER_ITEMS } from './mockPrStore';
import { poDatabase } from './mockPoStore';
import { inventoryCostLayerDatabase, grDatabase } from './mockGrStore';
import { loadPersistentData, savePersistentData } from './fileStorage';
import { syncItemToDatabase } from './dbBridge';

export interface ItemRecord {
  id: string;
  code: string;
  name: string;
  category: string;
  specs: string;
  defaultUomId: string;
  uomCode: string;
  defaultPrice: number;
  status: 'ACTIVE' | 'INACTIVE';
  description?: string;
  minStockLevel?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ItemSupplierPriceEntry {
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  poId: string;
  poNumber: string;
  poDate: string;
  unitPrice: number;
  orderedQty: number;
  uomCode: string;
  subtotal: number;
  poStatus: string;
}

export interface ItemInventoryEntry {
  inventoryId: string;
  condition: string;
  status: string;
  remainingQty: number;
  initialQty: number;
  uomCode: string;
  unitCost: number;
  warehouseLocation: string;
  batchNumber: string;
  grNumber: string;
  poNumber: string;
  supplierName: string;
  createdAt: string;
}

export interface ItemPurchaseHistoryEntry {
  grId: string;
  grNumber: string;
  grDate: string;
  poId: string;
  poNumber: string;
  supplierId: string;
  supplierName: string;
  receivedQty: number;
  rejectedQty: number;
  uomCode: string;
  unitCost: number;
  vendorDeliveryNote: string;
  receiverName: string;
}

export interface ItemDetailData {
  item: ItemRecord;
  kpis: {
    totalStockQty: number;
    availableQty: number;
    totalPurchased: number;
    totalSpend: number;
    suppliersCount: number;
    lastPurchaseDate: string | null;
    lastPurchasePrice: number | null;
  };
  supplierPrices: ItemSupplierPriceEntry[];
  purchaseHistory: ItemPurchaseHistoryEntry[];
  inventoryEntries: ItemInventoryEntry[];
}

// Build a live item db from MASTER_ITEMS with additional fields
const initialItemDatabase: ItemRecord[] = MASTER_ITEMS.map((m) => ({
  ...m,
  status: 'ACTIVE' as const,
  description: '',
  minStockLevel: 0,
  createdAt: '2026-01-01T00:00:00.000Z',
  updatedAt: '2026-09-14T00:00:00.000Z',
}));

export const itemDatabase: ItemRecord[] = loadPersistentData('item-database.json', initialItemDatabase);

function syncToMasterItems(record: ItemRecord) {
  const existingIdx = MASTER_ITEMS.findIndex((m) => m.id === record.id || m.code === record.code);
  const entry = {
    id: record.id,
    code: record.code,
    name: record.name,
    category: record.category,
    specs: record.specs,
    defaultUomId: record.defaultUomId,
    uomCode: record.uomCode,
    defaultPrice: record.defaultPrice,
  };
  if (existingIdx >= 0) {
    MASTER_ITEMS[existingIdx] = entry;
  } else {
    MASTER_ITEMS.push(entry);
  }
}

// Initial sync
for (const it of itemDatabase) {
  syncToMasterItems(it);
}

// ─── CRUD ───────────────────────────────────────────────────────────────────

export function getAllItems(search?: string, category?: string): ItemRecord[] {
  let list = [...itemDatabase];
  if (search) {
    const q = search.toLowerCase();
    list = list.filter(
      (i) =>
        i.code.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q) ||
        i.specs.toLowerCase().includes(q)
    );
  }
  if (category && category !== 'ALL') {
    list = list.filter((i) => i.category === category);
  }
  return list;
}

export function getItemById(id: string): ItemRecord | undefined {
  return itemDatabase.find(
    (i) => i.id === id || i.code.toLowerCase() === id.toLowerCase()
  );
}

export function createItem(data: Partial<ItemRecord>): ItemRecord {
  if (!data.code || !data.name || !data.category || !data.uomCode) {
    throw new Error('Kode, nama, kategori, dan satuan wajib diisi.');
  }
  const existing = itemDatabase.find(
    (i) => i.code.toLowerCase() === data.code!.toLowerCase()
  );
  if (existing) {
    throw new Error(`Item dengan kode '${data.code}' sudah terdaftar.`);
  }
  const newId = `itm-${Date.now()}`;
  const record: ItemRecord = {
    id: newId,
    code: data.code.toUpperCase(),
    name: data.name,
    category: data.category,
    specs: data.specs || '',
    defaultUomId: data.defaultUomId || 'uom-001',
    uomCode: data.uomCode,
    defaultPrice: data.defaultPrice || 0,
    status: data.status || 'ACTIVE',
    description: data.description || '',
    minStockLevel: data.minStockLevel || 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  itemDatabase.push(record);
  savePersistentData('item-database.json', itemDatabase);
  syncItemToDatabase(record);
  syncToMasterItems(record);
  return record;
}

export function updateItem(id: string, data: Partial<ItemRecord>): ItemRecord {
  const idx = itemDatabase.findIndex(
    (i) => i.id === id || i.code.toLowerCase() === id.toLowerCase()
  );
  if (idx === -1) throw new Error(`Item dengan ID '${id}' tidak ditemukan.`);

  if (data.code && data.code !== itemDatabase[idx].code) {
    const dup = itemDatabase.find(
      (i, j) => j !== idx && i.code.toLowerCase() === data.code!.toLowerCase()
    );
    if (dup) throw new Error(`Kode item '${data.code}' sudah digunakan item lain.`);
  }

  itemDatabase[idx] = {
    ...itemDatabase[idx],
    ...data,
    id: itemDatabase[idx].id,
    updatedAt: new Date().toISOString(),
  };
  savePersistentData('item-database.json', itemDatabase);
  syncItemToDatabase(itemDatabase[idx]);
  syncToMasterItems(itemDatabase[idx]);
  return itemDatabase[idx];
}

export function deleteItem(id: string): void {
  const idx = itemDatabase.findIndex(
    (i) => i.id === id || i.code.toLowerCase() === id.toLowerCase()
  );
  if (idx === -1) throw new Error(`Item dengan ID '${id}' tidak ditemukan.`);

  // Guard: prevent deletion if there are inventory records
  const hasInventory = inventoryCostLayerDatabase.some(
    (l) => l.itemId === itemDatabase[idx].id && l.remainingQty > 0
  );
  if (hasInventory) {
    throw new Error(
      `Item '${itemDatabase[idx].name}' memiliki stok tersisa di inventaris. Nonaktifkan item daripada menghapusnya.`
    );
  }

  const deleted = itemDatabase.splice(idx, 1)[0];
  savePersistentData('item-database.json', itemDatabase);
  const miIdx = MASTER_ITEMS.findIndex((m) => m.id === deleted.id || m.code === deleted.code);
  if (miIdx >= 0) {
    MASTER_ITEMS.splice(miIdx, 1);
  }
}

// ─── DETAIL WORKSPACE ────────────────────────────────────────────────────────

export function getItemDetailData(id: string): ItemDetailData {
  const item = getItemById(id);
  if (!item) throw new Error(`Item dengan ID '${id}' tidak ditemukan.`);

  // Supplier Prices: from all PO line items for this item
  const supplierPrices: ItemSupplierPriceEntry[] = [];
  for (const po of poDatabase) {
    for (const line of po.items) {
      if (line.itemId === item.id) {
        supplierPrices.push({
          supplierId: po.supplierId,
          supplierName: po.supplierSnapshot.name,
          supplierCode: po.supplierSnapshot.code,
          poId: po.id,
          poNumber: po.poNumber,
          poDate: po.createdAt,
          unitPrice: line.unitPrice,
          orderedQty: line.orderedQty,
          uomCode: line.uomCode,
          subtotal: line.subtotal,
          poStatus: po.status,
        });
      }
    }
  }

  // Purchase History: from GR records
  const purchaseHistory: ItemPurchaseHistoryEntry[] = [];
  for (const gr of grDatabase) {
    for (const line of gr.items) {
      if (line.itemId === item.id) {
        purchaseHistory.push({
          grId: gr.id,
          grNumber: gr.grNumber,
          grDate: gr.receiveDate,
          poId: gr.poId,
          poNumber: gr.poNumber,
          supplierId: gr.supplierId,
          supplierName: gr.supplierName,
          receivedQty: line.receivedQty,
          rejectedQty: line.rejectedQty,
          uomCode: line.uomCode,
          unitCost: line.unitCost,
          vendorDeliveryNote: gr.vendorDeliveryNote,
          receiverName: gr.receiverName,
        });
      }
    }
  }

  // Inventory Entries: from cost layer db
  const inventoryEntries: ItemInventoryEntry[] = inventoryCostLayerDatabase
    .filter((l) => l.itemId === item.id)
    .map((l) => ({
      inventoryId: l.inventoryId,
      condition: l.condition,
      status: l.status,
      remainingQty: l.remainingQty,
      initialQty: l.initialQty,
      uomCode: l.uomCode,
      unitCost: l.unitCost,
      warehouseLocation: l.warehouseLocation,
      batchNumber: l.batchNumber,
      grNumber: l.grNumber,
      poNumber: l.poNumber,
      supplierName: l.supplierName || '',
      createdAt: l.createdAt,
    }));

  // Unique suppliers
  const uniqueSupplierIds = new Set(supplierPrices.map((s) => s.supplierId));

  // KPIs
  const totalStockQty = inventoryEntries.reduce((s, e) => s + e.remainingQty, 0);
  const availableQty = inventoryEntries
    .filter((e) => e.status === 'AVAILABLE')
    .reduce((s, e) => s + e.remainingQty, 0);
  const totalPurchased = purchaseHistory.reduce((s, h) => s + h.receivedQty, 0);
  const totalSpend = supplierPrices.reduce((s, p) => s + p.subtotal, 0);

  const sortedPO = [...supplierPrices].sort(
    (a, b) => new Date(b.poDate).getTime() - new Date(a.poDate).getTime()
  );
  const lastPurchaseDate = sortedPO[0]?.poDate || null;
  const lastPurchasePrice = sortedPO[0]?.unitPrice || null;

  return {
    item,
    kpis: {
      totalStockQty,
      availableQty,
      totalPurchased,
      totalSpend,
      suppliersCount: uniqueSupplierIds.size,
      lastPurchaseDate,
      lastPurchasePrice,
    },
    supplierPrices,
    purchaseHistory,
    inventoryEntries,
  };
}
